import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import { tokenMonitor } from '../../lib/services/TokenMonitorService';

interface SessionWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
  onLogout: () => void;
  timeLeft: number;
}

export const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  isOpen,
  onClose,
  onRefresh,
  onLogout,
  timeLeft
}) => {
  const [countdown, setCountdown] = useState(timeLeft);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await tokenMonitor.forceRefresh();
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Failed to refresh session:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="glass-card max-w-md w-full animate-fade-in">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <AlertTriangle className="w-12 h-12 text-warning-500" />
          </div>
          <CardTitle className="text-xl font-bold text-text-primary">
            Session Expiring Soon
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <p className="text-text-secondary mb-4">
              Your session will expire in:
            </p>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Clock className="w-5 h-5 text-warning-500" />
              <span className="text-2xl font-bold text-warning-500">
                {formatTime(countdown)}
              </span>
            </div>
            <p className="text-sm text-text-tertiary">
              Click "Refresh Session" to extend your session, or you'll be logged out automatically.
            </p>
          </div>

          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onLogout}
              className="flex-1"
            >
              Logout Now
            </Button>
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex-1 bg-gradient-to-r from-primary-500 to-accent-500"
            >
              {isRefreshing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Session
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionWarningModal;
